"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState, useRef } from "react";
import { FaLink } from "react-icons/fa";
import { SlOptionsVertical } from "react-icons/sl";

import { BookmarkIcon } from "@/components/icons/BookmarkIcon";
import { LikeIcon } from "@/components/icons/LikeIcon";
import { useAuthContext } from "@/components/providers/AuthContextProvider";
import { useHandleClickOutside } from "@/hooks";
import { useMessage } from "@/components/providers/MessageProvider";
import { getLabel } from "@/data/labels";
import { socialShareOptions } from "@/data/social";
import { fetchActivityStatus, postBookmark, postLike } from "@/services/commonService";
import { QuoteDetailType } from "@/types/quotes";
import envConfig from "@/config/envConfig";
import { getAppLocaleFromPathname, getLocaleFromPathname, getLocalePrefix } from "@/utils/getLocaleFromPath";

interface LikeBookmarkProps {
  article: QuoteDetailType;
}

const getLocalizedSocialName = (name: string, language: string): string => {
  if (language !== "te") return name;
  const socialNames: Record<string, string> = {
    Facebook: "ఫేస్బుక్",
    Instagram: "ఇన్స్టాగ్రామ్",
    YouTube: "యూట్యూబ్",
    X: "X",
    Whatsapp: "వాట్సాప్",
    LinkedIn: "లింక్డ్ఇన్",
    Email: "ఇమెయిల్",
  };
  return socialNames[name] || name;
};

export default function LikeBookmarkSection({ article }: LikeBookmarkProps) {
  const [likeCount, setLikeCount] = useState(article?.likes || 0);
  const [isLiked, setIsLiked] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const articleId = String(article._id ?? article?.id);
  const { user, refreshAuthentication } = useAuthContext();
  const queryClient = useQueryClient();
  const router = useRouter();
  const pathName = usePathname();
  const locale = getLocaleFromPathname(pathName) || "te-IN";
  const localePrefix = getLocalePrefix(pathName);
  const language = getAppLocaleFromPathname(pathName, article?.language);
  const { showMessage } = useMessage();
  const articleUrl = `${localePrefix}/articles-periodicals/${article?.slug}`;

  useHandleClickOutside(dropdownRef, () => setIsDropdownOpen(false));

  const { data: activityStatus } = useQuery({
    queryKey: ["fetchArticleActivityStatus", articleId],
    queryFn: () => fetchActivityStatus({ contentId: articleId, contentType: "quotes" }),
    enabled: !!user,
  });

  const handleLike = async () => {
    try {
      if (!user) {
        const redirectUrl = encodeURIComponent(pathName);
        router.push(`${localePrefix}/login?r=${redirectUrl}`);
        return;
      }

      const newLikeState = !isLiked;
      await postLike({ type: "quotes", id: articleId, isLiked: newLikeState });
      setIsLiked(newLikeState);
      setLikeCount(prev => (newLikeState ? prev + 1 : prev - 1));
    } catch (err: unknown) {
      if ((err as Error & { status?: number })?.status === 401) refreshAuthentication();
    }
  };

  const handleBookmark = async () => {
    try {
      if (!user) {
        const redirectUrl = encodeURIComponent(pathName);
        router.push(`${localePrefix}/login?r=${redirectUrl}`);
        return;
      }

      const newBookmarkState = !isBookmarked;
      await postBookmark({ id: articleId, isBookmarked: newBookmarkState, type: "quotes" });
      setIsBookmarked(newBookmarkState);
      await queryClient.invalidateQueries({ queryKey: ["fetchAllBookmarks"] });
    } catch (err: unknown) {
      if ((err as Error & { status?: number })?.status === 401) refreshAuthentication();
    }
  };

  useEffect(() => {
    if (activityStatus) {
      setIsLiked(activityStatus?.isLiked);
      setLikeCount(activityStatus?.likesCount);
      setIsBookmarked(activityStatus?.isBookmarked);
    }
  }, [activityStatus]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(`${envConfig?.clientUrl}${articleUrl}`);
    showMessage("info", language === "te" ? "లింక్ క్లిప్‌బోర్డ్‌కు కాపీ చేయబడింది!" : "Link copied to clipboard!");
  };

  return (
    <div className="flex flex-col gap-2 items-center sm:flex-row sm:justify-between sm:items-center w-full py-5">
      {/* Social share icons */}
      <div className="flex items-center justify-center gap-2">
        {socialShareOptions?.slice(0, 4)?.map((item, index) => {
          const Icon = item?.icon;
          const shareLink = item.generateLink(`${envConfig.clientUrl}${articleUrl}`, article?.title);
          return (
            <a
              key={index}
              href={shareLink}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Share on ${item.name}`}
              className="size-[22px] lg:size-[20px] rounded-md shrink-0 overflow-hidden bg-transparent flex justify-center items-center text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
            >
              <Icon className="w-full h-full" />
            </a>
          );
        })}
        <div className="h-4 w-px bg-gray-400 dark:bg-gray-500" />
        <div className="relative">
          <button
            className="size-[20px] flex justify-center text-[#585858] dark:text-white items-center"
            onClick={() => setIsDropdownOpen(true)}
            aria-label="More Options"
          >
            <SlOptionsVertical size={14} />
          </button>
          {isDropdownOpen && (
            <div
              ref={dropdownRef}
              className="absolute top-[100%] right-0 white shadow-md z-10 bg-white dark:bg-[#444] dark:text-white min-w-[180px] py-3 rounded"
            >
              <div
                className="flex items-center gap-2 cursor-pointer px-4 py-2 transition-colors hover:text-[#0055a5]"
                onClick={handleCopyLink}
              >
                <span className="flex items-center justify-center">
                  <FaLink />
                </span>
                <span className="text-sm font-medium whitespace-nowrap">
                  {language === "te" ? "లింక్ కాపీ చేయండి" : "Copy Link"}
                </span>
              </div>
              {socialShareOptions?.map((item, index) => {
                const Icon = item?.icon;
                const shareLink = item.generateLink(`${envConfig.clientUrl}${articleUrl}`, article?.title);
                const localizedName = getLocalizedSocialName(item.name, language);
                return (
                  <a
                    key={index}
                    href={shareLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Share on ${item.name}`}
                    className="flex items-center gap-2 px-4 py-2 transition-colors hover:text-[#0055a5]"
                  >
                    <span className="flex items-center justify-center w-5 h-5">
                      <Icon className="w-full h-full" />
                    </span>
                    <span className="text-sm font-medium whitespace-nowrap">{localizedName}</span>
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-center gap-3 flex-shrink-0">
        <button
          className="flex items-center gap-1.5 text-gray-900 dark:text-white"
          onClick={handleBookmark}
          aria-label="Bookmark"
        >
          <BookmarkIcon
            className="w-3 h-3 shrink-0"
            fill={isBookmarked ? "#3b82f6" : "currentColor"}
          />
          <span className="text-xs font-medium text-gray-900 dark:text-white">
            {getLabel(language, "BOOKMARK")}
          </span>
        </button>
        <button
          className="flex items-center gap-1.5 text-gray-900 dark:text-white"
          onClick={handleLike}
          aria-label="Like"
        >
          <LikeIcon
            className="w-3 h-3 shrink-0"
            fill={isLiked ? "#3b82f6" : "currentColor"}
          />
          <span className="text-xs font-medium text-gray-900 dark:text-white">
            {likeCount} {getLabel(language, "LIKES")}
          </span>
        </button>
        {/* <div className="flex items-center gap-1">
            <BiDislike size={16} color="#66676B" />
            <Typography variant="body2" color="#66676B" className="font-medium">
              11 Dislikes
            </Typography>
          </div> */}
      </div>
    </div>
  );
}
